import { AssetFormServiceInstance } from 'services/AssetFormService'
import { Country } from 'types/userManagement'
import { PRODUCT_FIELD_MAPPINGS } from 'utils/aiAssistantUtils'
import { isPackshotLandingPageAssetType } from 'utils/landingPageAssetFormFieldVisibility'
import { LP_BANNER_COPY_FIELD_LABELS } from 'utils/landingPageBannerCopyLabels'
import {
  isLeadLandingPageAssetType,
  LP_LEAD_COPY_FIELD_LABELS
} from 'utils/landingPageLeadCopyLabels'
import {
  isLandingPageLogoAssetType,
  LP_LOGO_FIELD_LABELS
} from 'utils/landingPageLogoCopyLabels'
import { formatAssetName } from 'utils/strings'

import {
  AI_COUNTRY_LANGUAGE_MAPPING,
  FieldConfig,
  ImageConfig,
  Language} from '../types/ai-assistant.types'

/** CMS file/URL slots must not be offered as AI text fields (alts are mapped separately). */
const LP_AI_NON_GENERATABLE_CMS_FIELDS = new Set([
  'sku',
  'image',
  'imageUrl',
  'largeImage',
  'largeImageUrl',
  'landingPageFile',
  'landingPageFileUrl',
  'activeImage',
  'activeImageUrl',
  'sourceGraphicFile',
  'sourceGraphicFileUrl',
  'video',
  'videoUrl',
  'pdfFile',
  'pdfFileUrl',
  'packshot',
  'packshotUrl',
  'openedPackshot',
  'openedPackshotUrl',
  'hotpoints',
  'hotpointsUrl',
  'hotpointsMobile',
  'hotpointsMobileUrl',
  'hotpointsProperties',
  'hotpointsMobileProperties',
  'hotpointTemplate',
  'country',
  'isActive'
])

/**
 * Map country code to AI-supported language using AI_COUNTRY_LANGUAGE_MAPPING
 * Uses proper mapping from SindyCat countries to languages that AI actually supports
 */
export const getLanguageFromCountry = (country: string): Language => {
  const countryCode = country.toUpperCase() as Country

  // All SindyCat countries are supported
  return AI_COUNTRY_LANGUAGE_MAPPING[countryCode]
}

/**
 * Superset of field names the LP AI backend may accept (per fieldName in fieldsToGenerate).
 * Actual fields shown per asset type come from {@link getLandingPageAssetSupportedFields}.
 */
export const LP_ASSET_FIELD_NAMES = [
  'name',
  'additionalText',
  'attributeAlt',
  'activeImageAlt',
  'attributeAltMobile',
  'defaultDescription',
  'descriptionForEuro',
  'descriptionForTIM',
  'bannerTitle',
  'bannerTitleAccent',
  'bannerSubtitle',
  'bannerDisclaimer',
  'hotpointsAlt',
  'hotpointsMobileAlt',
  'videoTitle',
  'packshotAlt',
  'openedPackshotAlt'
] as const

/**
 * LP asset AI fields for the current type + brand: same rules as product assets
 * ({@link getAssetSupportedFields}) plus LP-only fields (e.g. videoTitle for video, hotpoints alts).
 */
export const getLandingPageAssetSupportedFields = (
  assetTypeRaw: string,
  brand: string,
  brandSpecificFields?: Array<{ name: string; label: string; type?: string }>
): string[] => {
  const assetType = formatAssetName(assetTypeRaw)
  const supportedFields: string[] = ['name']

  const fieldMappings: Record<string, string> = {
    additionalText: 'additionalText',
    defaultDescription: 'defaultDescription',
    descriptionForEuro: 'descriptionForEuro',
    attributeAlt: 'image',
    activeImageAlt: 'activeImage',
    attributeAltMobile: 'largeImage',
    packshotAlt: 'packshot',
    openedPackshotAlt: 'openedPackshot'
  }

  Object.entries(fieldMappings).forEach(([aiFieldName, assetFormFieldName]) => {
    if (
      AssetFormServiceInstance.isFieldAllowedInAssetType(
        assetType,
        assetFormFieldName,
        brand
      )
    ) {
      supportedFields.push(aiFieldName)
    }
  })

  ;(
    [
      'bannerTitle',
      'bannerTitleAccent',
      'bannerSubtitle',
      'bannerDisclaimer'
    ] as const
  ).forEach(fieldName => {
    if (
      AssetFormServiceInstance.isFieldAllowedInAssetType(assetType, fieldName, brand)
    ) {
      supportedFields.push(fieldName)
    }
  })

  if (assetType === 'video') {
    supportedFields.push('videoTitle')
  }

  if (assetType === 'hotpoints') {
    supportedFields.push('hotpointsAlt', 'hotpointsMobileAlt')
  }

  if (isPackshotLandingPageAssetType(assetTypeRaw, brand)) {
    ;(['packshotAlt', 'openedPackshotAlt'] as const).forEach(fieldName => {
      const formField = fieldMappings[fieldName] ?? fieldName

      if (
        AssetFormServiceInstance.isFieldAllowedInAssetType(
          assetType,
          formField,
          brand
        ) &&
        !supportedFields.includes(fieldName)
      ) {
        supportedFields.push(fieldName)
      }
    })
  }

  if (brandSpecificFields && brandSpecificFields.length > 0) {
    brandSpecificFields.forEach(field => {
      if (LP_AI_NON_GENERATABLE_CMS_FIELDS.has(field.name)) return
      if (!supportedFields.includes(field.name)) {
        supportedFields.push(field.name)
      }
    })
  }

  return supportedFields
}

/**
 * Snapshot of form values for LP AI (existingContent): only fields allowed for this LP asset type.
 */
export const getLandingPageAssetFormValuesForAi = (
  assetTypeName: string | undefined,
  brandName: string | undefined,
  getValues: (name?: string | string[]) => any,
  brandSpecificFields?: Array<{ name: string; label: string; type?: string }>
): Record<string, string> => {
  if (!assetTypeName) {
    return {}
  }

  const keys = getLandingPageAssetSupportedFields(
    assetTypeName,
    brandName ?? '',
    brandSpecificFields
  )
  const allFormValues = getValues()
  const out: Record<string, string> = {}

  keys.forEach(fieldName => {
    if (fieldName === 'sku') return
    const v = allFormValues[fieldName]

    out[fieldName] = v !== undefined && v !== null ? String(v) : ''
  })

  return out
}

/**
 * Get supported field names based on context type and asset type
 * For assets, this returns ALL possible fields - filtering by brand/asset type should be done in the component
 * For products, only returns alt fields, dimensions, and summary fields
 */
export const getSupportedFields = (contextType: string): string[] => {
  if (contextType === 'product') {
    // Only return alt fields, dimensions, and summary fields for products
    return [
      'packshotAlt',
      'openedPackshotAlt',
      'hotpointsAlt',
      'hotpointsMobileAlt',
      'dimensionsAlt',
      'dimensionsTechnicalDrawingAlt',
      'dimensionsDescription',
      'summaryAlt',
      'summaryDescription'
    ]
  }

  // Asset fields - return all possible fields, filtering will be done by the component
  const baseFields = [
    'name',
    'defaultDescription',
    'descriptionForEuro',
    'descriptionForTIM',
    'additionalText'
  ]

  // Fields that require images
  const imageFields = ['attributeAlt', 'activeImageAlt', 'attributeAltMobile']

  return [...baseFields, ...imageFields]
}

/**
 * Get supported field names for assets based on brand and asset type
 * This function should be used when brand information is available
 */
export const getAssetSupportedFields = (
  assetType: string,
  brand: string,
  isFieldAllowedFn: (
    assetType: string,
    fieldName: string,
    brand: string
  ) => boolean,
  brandSpecificFields?: Array<{ name: string; label: string; type?: string }>
): string[] => {
  const supportedFields = ['name'] // Name is always supported

  // Field mappings: AI Assistant field name -> AssetFormService field name
  const fieldMappings = {
    additionalText: 'additionalText',
    defaultDescription: 'defaultDescription',
    descriptionForEuro: 'descriptionForEuro',
    attributeAlt: 'image', // Alt text is tied to image field
    activeImageAlt: 'activeImage',
    attributeAltMobile: 'largeImage' // Mobile alt text is tied to largeImage field
  }

  // Check each field
  Object.entries(fieldMappings).forEach(([aiFieldName, assetFormFieldName]) => {
    if (isFieldAllowedFn(assetType, assetFormFieldName, brand)) {
      supportedFields.push(aiFieldName)
    }
  })

  // Add brand-specific fields (like descriptionForTIM)
  if (brandSpecificFields && brandSpecificFields.length > 0) {
    brandSpecificFields.forEach(field => {
      if (!supportedFields.includes(field.name)) {
        supportedFields.push(field.name)
      }
    })
  }

  return supportedFields
}

/**
 * Check if field requires an image
 */
export const fieldRequiresImage = (fieldName: string): boolean => {
  // Asset fields that require images
  const assetImageRequiredFields = [
    'attributeAlt',
    'activeImageAlt',
    'attributeAltMobile'
  ]

  // Product / LP asset alt fields that require their corresponding images
  const imageAltFields = [
    'packshotAlt', // requires packshot image
    'openedPackshotAlt', // requires openedPackshot image
    'hotpointsAlt', // requires hotpoints image (product + LP asset)
    'hotpointsMobileAlt', // requires hotpointsMobile image (product + LP asset)
    'dimensionsAlt', // requires dimensions image
    'dimensionsTechnicalDrawingAlt', // requires technical drawing image
    'summaryAlt' // requires summary image
  ]

  return (
    assetImageRequiredFields.includes(fieldName) ||
    imageAltFields.includes(fieldName)
  )
}

/**
 * Get fields that need images (for assignment dialog)
 * Returns fields that require images but don't have them assigned yet
 * Excludes fields that already have form images (source === 'existing')
 */
export const getFieldsNeedingImages = (
  fields: FieldConfig[],
  imageConfigs: ImageConfig[]
): FieldConfig[] => {
  // Get fields that have form images (source === 'existing') - these should be excluded
  const fieldsWithFormImages = new Set(
    imageConfigs
      .filter(config => config.source === 'existing' && config.assignedToField)
      .map(config => config.assignedToField!)
  )

  // Get fields that have uploaded/url images assigned
  const fieldsWithAssignedImages = new Set(
    imageConfigs
      .filter(config => config.assignedToField && config.source !== 'existing')
      .map(config => config.assignedToField!)
  )

  return fields.filter(field => {
    // Only include fields that require images
    if (!field.requiresImage) return false

    // Exclude fields that already have form images (auto-assigned)
    if (fieldsWithFormImages.has(field.fieldName)) return false

    // Include fields that don't have assigned images
    // (Even if context images exist, user might want to assign dedicated image)
    return !fieldsWithAssignedImages.has(field.fieldName)
  })
}

/**
 * Validate field name for given context
 */
export const validateFieldForContext = (
  fieldName: string,
  contextType: string
): boolean => {
  const supportedFields = getSupportedFields(contextType)

  return supportedFields.includes(fieldName)
}

/**
 * Generate unique ID for image config
 */
export const generateImageConfigId = (): string => {
  return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Create preview URL for uploaded file
 */
export const createPreviewUrl = (file: File): string => {
  return URL.createObjectURL(file)
}

/**
 * Cleanup preview URL
 */
export const cleanupPreviewUrl = (url: string): void => {
  URL.revokeObjectURL(url)
}

/**
 * Validate image file
 */
export const validateImageFile = (
  file: File
): { isValid: boolean; error?: string } => {
  const maxSize = 10 * 1024 * 1024 // 10MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

  if (file.size > maxSize) {
    return { isValid: false, error: 'File size must be less than 10MB' }
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Only JPEG, PNG and WebP images are allowed'
    }
  }

  return { isValid: true }
}

/**
 * Convert file to base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      // Remove data:image/jpeg;base64, prefix
      const base64 = result.split(',')[1]

      resolve(base64)
    }
    reader.onerror = error => reject(error)
  })
}

const ADDITIONAL_TEXT_TITLE_ASSET_TYPES = new Set(['intro', 'hotpoints'])

const isIconsAssetType = (assetType?: string): boolean => {
  if (!assetType) return false
  const normalized = formatAssetName(assetType)

  return normalized === 'icons' || normalized === 'icon'
}

const isAdditionalTextTitleAssetType = (assetType?: string): boolean => {
  if (!assetType) return false

  return ADDITIONAL_TEXT_TITLE_ASSET_TYPES.has(formatAssetName(assetType))
}

/**
 * Get user-friendly field label
 */
export const getFieldLabel = (
  fieldName: string,
  brandSpecificFields?: Array<{ name: string; label: string; type?: string }>,
  assetType?: string
): string => {
  // Check if it's a brand-specific field first
  if (brandSpecificFields) {
    const brandField = brandSpecificFields.find(f => f.name === fieldName)

    if (brandField) {
      return brandField.label
    }
  }

  if (fieldName === 'additionalText' && isAdditionalTextTitleAssetType(assetType)) {
    return 'Title'
  }

  if (isLeadLandingPageAssetType(assetType)) {
    if (fieldName in LP_LEAD_COPY_FIELD_LABELS) {
      return LP_LEAD_COPY_FIELD_LABELS[fieldName as keyof typeof LP_LEAD_COPY_FIELD_LABELS]
    }
  }

  if (isIconsAssetType(assetType)) {
    if (fieldName === 'attributeAlt') {
      return 'Alternative text for file'
    }

    if (fieldName === 'activeImageAlt') {
      return 'Alternative text for active image'
    }
  }

  if (isLandingPageLogoAssetType(assetType)) {
    if (fieldName === 'attributeAlt') {
      return LP_LOGO_FIELD_LABELS.attributeAlt
    }

    if (fieldName === 'attributeAltMobile') {
      return LP_LOGO_FIELD_LABELS.attributeAltMobile
    }
  }

  const labelMap: Record<string, string> = {
    // Asset fields
    name: 'Name',
    shortName: 'Short Name',
    description: 'Description',
    defaultDescription: 'Description For Others',
    descriptionForEuro: 'Description For Euro',
    descriptionForTIM: 'Description For TIM',
    attributeAlt: 'Alternative text for image',
    activeImageAlt: 'Alternative text for active image',
    attributeAltMobile: 'Alternative text for mobile image',
    ...LP_BANNER_COPY_FIELD_LABELS,

    // Product fields
    sku: 'SKU',
    skuCodes: 'SKU Codes',
    packshotAlt: 'Alternative Text for Packshot Image',
    openedPackshotAlt: 'Alternative Text for Opened Packshot Image',
    dimensionsDescription: 'Dimensions Description',
    dimensionsAlt: 'Alternative Text for Dimensions Image',
    dimensionsTechnicalDrawingAlt:
      'Alternative Text for Technical Drawing Image',
    summaryDescription: 'Summary Description',
    summaryAlt: 'Alternative Text for Summary Image',
    hotpointsAlt: 'Alternative Text for Hotpoints Image',
    hotpointsMobileAlt: 'Alternative Text for Hotpoints Mobile Image',
    videoTitle: 'Video Title',
    mainVideoTitle: 'Main Video Title',
    mainVideoYoutubeId: 'Main Video YouTube ID',
    bottomVideoYoutubeId: 'Bottom Video YouTube ID'
  }

  return labelMap[fieldName] || fieldName
}

/**
 * Get character limit for a field based on field name and asset type
 */
export const getFieldCharacterLimit = (
  fieldName: string,
  assetType?: string
): number => {
  // Alt fields: 200 characters
  const altFields = [
    'attributeAlt',
    'activeImageAlt',
    'attributeAltMobile',
    'packshotAlt',
    'openedPackshotAlt',
    'hotpointsAlt',
    'hotpointsMobileAlt',
    'dimensionsAlt',
    'dimensionsTechnicalDrawingAlt',
    'summaryAlt'
  ]

  if (altFields.includes(fieldName)) {
    return 200
  }

  // Name field: always 250 characters
  if (fieldName === 'name') {
    return 250
  }

  // Additional text: short accent on most types; long disclaimer on Lead
  if (fieldName === 'additionalText') {
    return isLeadLandingPageAssetType(assetType) ? 1000 : 200
  }

  // Description fields: 1000 characters normally, 500 for icons asset type
  const descriptionFields = [
    'defaultDescription',
    'descriptionForEuro',
    'descriptionForTIM',
    'dimensionsDescription',
    'summaryDescription',
    'bannerTitle',
    'bannerTitleAccent',
    'bannerSubtitle',
    'bannerDisclaimer'
  ]

  if (descriptionFields.includes(fieldName)) {
    // Check if asset type is "icons" (case-insensitive)
    if (assetType && assetType.toLowerCase() === 'icons') {
      return 500
    }

    return 1000
  }

  // Other fields: 1000 characters
  return 1000
}

/**
 * Get color for character counter based on field type and current length
 * Returns: 'default' | 'orange' | 'red'
 */
export const getCharacterCounterColor = (
  fieldName: string,
  currentLength: number,
  assetType?: string
): 'default' | 'orange' | 'red' => {
  const limit = getFieldCharacterLimit(fieldName, assetType)

  // Alt fields: orange at 125, red at 200
  const altFields = [
    'attributeAlt',
    'activeImageAlt',
    'attributeAltMobile',
    'packshotAlt',
    'openedPackshotAlt',
    'hotpointsAlt',
    'hotpointsMobileAlt',
    'dimensionsAlt',
    'dimensionsTechnicalDrawingAlt',
    'summaryAlt'
  ]

  if (altFields.includes(fieldName)) {
    // Alt fields: orange at 125-199, red at exactly 200
    if (currentLength >= limit) {
      return 'red'
    }

    if (currentLength >= 125 && currentLength < limit) {
      return 'orange'
    }
  } else {
    // For other fields, red only at limit
    if (currentLength >= limit) {
      return 'red'
    }
  }

  return 'default'
}

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp)

  return date.toLocaleString()
}

/**
 * Calculate session expiry
 */
export const isSessionExpired = (expiresAt: string): boolean => {
  return new Date() > new Date(expiresAt)
}

/**
 * Field groups for product context
 */
export type FieldGroup = {
  title: string
  fields: string[]
}

/**
 * Get field groups for product context
 * Groups fields into: Alt Fields, Dimensions Description, Summary Description
 */
export const getProductFieldGroups = (): FieldGroup[] => {
  return [
    {
      title: 'Alt Fields',
      fields: [
        'packshotAlt',
        'openedPackshotAlt',
        'hotpointsAlt',
        'hotpointsMobileAlt'
      ]
    },
    {
      title: 'Dimensions & Summary',
      fields: [
        'dimensionsAlt',
        'dimensionsTechnicalDrawingAlt',
        'dimensionsDescription',
        'summaryAlt',
        'summaryDescription'
      ]
    }
  ]
}

/**
 * Get the group name for a field (for product context)
 */
export const getFieldGroup = (fieldName: string): string | null => {
  const groups = getProductFieldGroups()

  for (const group of groups) {
    if (group.fields.includes(fieldName)) {
      return group.title
    }
  }

  return null
}

/**
 * Get product field order matching visual order on product form page
 * Order: packshotAlt, openedPackshotAlt, hotpointsAlt, hotpointsMobileAlt,
 *        dimensionsAlt, dimensionsDescription, summaryAlt, summaryDescription
 */
export const getProductFieldOrder = (): string[] => {
  return [
    'packshotAlt',
    'openedPackshotAlt',
    'hotpointsAlt',
    'hotpointsMobileAlt',
    'dimensionsAlt',
    'dimensionsTechnicalDrawingAlt',
    'dimensionsDescription',
    'summaryAlt',
    'summaryDescription'
  ]
}

/**
 * Get field order for assets (matches form order)
 */
export const getAssetFieldOrder = (): string[] => {
  return [
    'name',
    'packshotAlt',
    'openedPackshotAlt',
    'additionalText',
    'attributeAlt',
    'activeImageAlt',
    'attributeAltMobile',
    'defaultDescription',
    'descriptionForEuro',
    'videoTitle',
    'hotpointsAlt',
    'hotpointsMobileAlt'
    // Brand-specific fields (like descriptionForTIM) will be added after these
  ]
}

/**
 * Get image URL for a field from availableImages array
 * Matches by fieldName property
 */
export const getFieldImageFromAvailableImages = (
  fieldName: string,
  availableImages: Array<{ url: string; alt?: string; fieldName?: string }>
): string | null => {
  const image = availableImages.find(img => img.fieldName === fieldName)

  return image?.url || null
}

/**
 * Get video URL for a field from availableVideos array
 * Matches by fieldName property
 */
export const getFieldVideoFromAvailableVideos = (
  fieldName: string,
  availableVideos: Array<{ url: string; alt?: string; fieldName?: string }>
): string | null => {
  const video = availableVideos.find(vid => vid.fieldName === fieldName)

  return video?.url || null
}
